/**
 * Dev-tools billing/tier/trial override.
 *
 * Mirrors the flags-dev pattern (`src/features/flags-dev/overrides.ts`):
 * a sessionStorage-backed Jotai atom holding one active override, applied
 * inside `useQueryUserBillingInfo`'s `select` so every derived hook
 * (`useReverseTrial`, `useQueryUserTier`, `useIsFreeUser`, both upgrade
 * CTAs, paywall modals, …) reflects the overridden state instantly — no
 * refresh needed, and a real refetch can't wipe it because the merge
 * happens on read, not in the cache.
 *
 * Persistence: sessionStorage — survives a hard reload but dies with the
 * tab, avoiding the "forgotten override follows me forever" footgun.
 *
 * Production safety: `applyBillingDevOverride` is a no-op whenever
 * `getEnvironment()` reports production, read per call, so even a
 * hand-crafted sessionStorage entry does nothing in prod.
 */
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import type { BillingSeatType } from '@heidi/schema/ml-scribe';

import type { UserBillingInfo } from '../../contracts';
import type { QueryUserBillingInfoResponse } from '../query/useQueryUserBillingInfo';

const isNonProductionEnvironment = (): boolean => getEnvironment() !== ENV_NAME.PROD;

type EvidenceBilling = NonNullable<UserBillingInfo['evidence_billing']>;

/**
 * Field-level patch merged over the real billing-info response. Only the
 * keys present replace real values; `evidence_billing` deep-merges so its
 * required fields (tier, free_trial_history, …) keep their real values.
 */
export type BillingDevPatch = Omit<
  Partial<QueryUserBillingInfoResponse>,
  'evidence_billing'
> & {
  evidence_billing?: Partial<EvidenceBilling>;
};

export type BillingDevTier = 'free' | 'clinician' | 'practice' | 'scribeTeam';

/** Controls in the dev-tools billing section. Persisted so a reload rehydrates them. */
export interface BillingDevForm {
  tier: BillingDevTier;
  seatType: BillingSeatType;
  trialing: boolean;
  trialDaysLeft: number;
  trialHoursLeft: number;
  /** Empty trial history → "Try Clinician for free". Ignored while `trialing`. */
  trialAvailable: boolean;
}

export interface BillingDevOverride {
  /** Human label surfaced in the dev section. */
  label: string;
  form: BillingDevForm;
  patch: BillingDevPatch;
}

export const BILLING_DEV_OVERRIDE_STORAGE_KEY = 'heidi.devTools.billingOverride';

/** SSR-safe sessionStorage adapter (atom is read during Next.js SSR). */
const sessionStorageAdapter = createJSONStorage<BillingDevOverride | null>(() => {
  if (typeof window === 'undefined') {
    return {
      getItem: () => null,
      setItem: () => undefined,
      removeItem: () => undefined,
    };
  }
  return window.sessionStorage;
});

export const billingDevOverrideAtom = atomWithStorage<BillingDevOverride | null>(
  BILLING_DEV_OVERRIDE_STORAGE_KEY,
  null,
  sessionStorageAdapter
);

/**
 * Merge the active dev override over the real billing response. Identity
 * when no override is active (and always in production builds).
 *
 * Works with a `null` real response too (e.g. an account whose billing
 * call returns nothing) so presets stay usable on any dev account.
 */
export function applyBillingDevOverride(
  data: QueryUserBillingInfoResponse | null | undefined,
  override: BillingDevOverride | null
): QueryUserBillingInfoResponse | null | undefined {
  if (!isNonProductionEnvironment() || !override) {
    return data;
  }
  const { evidence_billing: evidencePatch, ...patch } = override.patch;
  const merged = { ...data, ...patch };
  if (evidencePatch) {
    // Dev-only cast: the partial keeps required EvidenceBilling fields from
    // the real response when present; downstream trial hooks only read
    // `trial_end_date` / `is_in_free_trial`, which the presets always set.
    merged.evidence_billing = {
      ...data?.evidence_billing,
      ...evidencePatch,
    } as EvidenceBilling;
  }
  return merged as QueryUserBillingInfoResponse;
}
